// Where an item is at: animated stage tracker, one-click status menu, who the onus is on, and the back & forth so far.
import { useEffect, useRef, useState } from 'react';
import { isClosed, stageIndex, stagesFor, statusLabel, STATUS, type Item, type RegisterDef } from '../../lib/registers';
import { daysSince, dueText, orgShort, plural, type Org } from './shared';

export type Response = {
  id: string;
  kind: 'response' | 'request' | 'note';
  request_type: string | null;
  from_org_id: string | null;
  to_org_id: string | null;
  sets_onus: boolean;
  onus_org_id: string | null;
  body: string;
  via: string | null;
  received_on: string | null;
  is_interim: boolean;
  is_final: boolean;
  created_at: string;
  created_by: string | null;
  deleted_at: string | null;
};

type Mode = 'cur' | 'wait' | 'late' | 'hold' | 'fin' | 'rej';

/** How the current stage should look (and move). */
export function stageMode(item: Item, swiId: string | undefined): Mode {
  if (item.status === 'rejected' || item.status === 'void') return 'rej';
  if (item.status === 'hold') return 'hold';
  const st = stagesFor(item);
  if (stageIndex(item) === st.length - 1 && isClosed(item.status)) return 'fin';
  if (item.respond_by && dueText(item.respond_by)?.tone === 'late' && !isClosed(item.status)) return 'late';
  if (item.onus_org_id && item.onus_org_id !== swiId) return 'wait';
  return 'cur';
}

export function StageTracker({
  item,
  orgs,
  swiId,
  canEdit,
  rounds,
  onPick,
}: {
  item: Item;
  orgs: Map<string, Org>;
  swiId: string | undefined;
  canEdit: boolean;
  rounds: number;
  onPick: (stageKey: string) => void;
}) {
  const st = stagesFor(item);
  const at = stageIndex(item);
  const mode = stageMode(item, swiId);
  const onus = item.onus_org_id ? orgs.get(item.onus_org_id) : undefined;
  const since = daysSince(item.onus_since);
  const due = dueText(item.respond_by);
  // the last stage "pops" once when it is reached
  const [popped, setPopped] = useState(false);
  const prev = useRef(at);
  useEffect(() => {
    if (at !== prev.current && mode === 'fin') setPopped(true);
    prev.current = at;
  }, [at, mode]);

  return (
    <ol className={`stages mode-${mode}`} aria-label="Where this item is at">
      {st.map((s, i) => {
        // V0.4.9: where we stand is green (done); the NEXT step is the one that pulses — amber while waiting,
        // red when late. Hold / rejected stay on the current stage.
        const stopped = mode === 'hold' || mode === 'rej';
        const live = stopped ? at : at + 1; // the stage that carries the mode
        const cls = mode === 'fin' ? (i <= at ? 'done' : 'todo') : i === live ? mode : i <= at ? 'done' : 'todo';
        let sub = '';
        if (i === at && !stopped && mode !== 'fin') sub = 'Now';
        if (i === live && mode !== 'fin') {
          if (mode === 'wait' && onus) sub = `${orgShort(onus)}${since !== null ? ` · ${plural(since, 'day')}` : ''}`;
          else if (mode === 'late') sub = due?.text ?? 'overdue';
          else if (mode === 'hold') sub = 'On hold';
          else if (mode === 'rej') sub = statusLabel(item.status, item.type);
          else if (mode === 'cur') sub = rounds > 1 ? `Round ${rounds}` : due && !isClosed(item.status) ? due.text : '';
        }
        const mark = cls === 'done' ? '✓' : cls === 'rej' ? '✕' : cls === 'hold' ? '' : String(i + 1);
        return (
          <li key={s.key} className={`stg ${cls} ${i === 0 ? 'first' : ''} ${i === at ? 'here' : ''} ${i === at && popped ? 'pop' : ''}`}>
            <span className="bar" aria-hidden="true" />
            <button
              type="button"
              className="dot"
              disabled={!canEdit}
              aria-current={i === at ? 'step' : undefined}
              aria-label={`${s.label}${i === at ? ' (current)' : i < at ? ' (done)' : ''}${canEdit ? ' — set this stage' : ''}`}
              onClick={() => i !== at && onPick(s.key)}
            >
              {mark}
            </button>
            <span className="lbl">{s.label}</span>
            {sub ? <span className="sub">{sub}</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

export function StatusMenu({ item, def, canEdit, onPick }: { item: Item; def: RegisterDef; canEdit: boolean; onPick: (status: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const tone = STATUS[item.status]?.tone ?? 'neutral';
  return (
    <div className="status-menu" ref={ref}>
      <button type="button" className={`status-btn tone-${tone}`} aria-haspopup="menu" aria-expanded={open} disabled={!canEdit} onClick={() => setOpen(!open)}>
        <span className="sdot" aria-hidden="true" />
        {statusLabel(item.status, item.type)}
        {canEdit ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
        ) : null}
      </button>
      {open ? (
        <div className="status-pop" role="menu">
          {def.statuses.map((s) => (
            <button
              key={s}
              type="button"
              role="menuitemradio"
              aria-checked={s === item.status}
              className={s === item.status ? 'on' : ''}
              onClick={() => {
                setOpen(false);
                if (s !== item.status) onPick(s);
              }}
            >
              <span className={`sdot tone-${STATUS[s]?.tone ?? 'neutral'}`} aria-hidden="true" />
              {statusLabel(s, item.type)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** "Onus is on" chips: one click says who we are waiting on. */
export function OnusBar({
  item,
  orgs,
  allOrgs,
  swiId,
  pbsId,
  canEdit,
  onPick,
}: {
  item: Item;
  orgs: Map<string, Org>;
  allOrgs: Org[];
  swiId: string | undefined;
  pbsId: string | undefined;
  canEdit: boolean;
  onPick: (orgId: string | null) => void;
}) {
  const ids = [swiId, pbsId, item.org_id, ...item.directed_to, item.onus_org_id].filter((x): x is string => Boolean(x));
  const parties = [...new Set(ids)].map((id) => orgs.get(id)).filter((o): o is Org => Boolean(o));
  const [other, setOther] = useState(false);
  return (
    <div className="onus" role="radiogroup" aria-label="Who the onus is on">
      <span className="k">Onus is on</span>
      {parties.map((o) => {
        const on = o.id === item.onus_org_id;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={!canEdit}
            className={`court ${on ? 'on' : ''} ${o.id === swiId ? 'swi' : ''}`}
            onClick={() => onPick(on ? null : o.id)}
            title={on ? 'Click again to clear' : `Set the onus on ${o.name}`}
          >
            {orgShort(o)}
          </button>
        );
      })}
      {canEdit ? (
        other ? (
          <select
            autoFocus
            aria-label="Someone else"
            defaultValue=""
            onBlur={() => setOther(false)}
            onChange={(e) => {
              setOther(false);
              if (e.target.value) onPick(e.target.value);
            }}
          >
            <option value="">Choose…</option>
            {allOrgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        ) : (
          <button type="button" className="court ghost" onClick={() => setOther(true)}>
            + Other
          </button>
        )
      ) : null}
      {!item.onus_org_id ? <span className="muted small">No one at the moment</span> : null}
    </div>
  );
}

/** The back & forth so far: one hop per response, ending in who has it now. */
export function BackAndForth({ item, responses, orgs }: { item: Item; responses: Response[]; orgs: Map<string, Org> }) {
  const list = [...responses].filter((r) => !r.deleted_at).sort((a, b) => (a.received_on ?? a.created_at).localeCompare(b.received_on ?? b.created_at));
  if (!list.length && !item.onus_org_id) return null;
  const onus = item.onus_org_id ? orgs.get(item.onus_org_id) : undefined;
  const since = daysSince(item.onus_since);
  return (
    <div className="chain" aria-label="Back and forth so far">
      <span className="k">Back &amp; forth</span>
      {list.map((r, i) => (
        <span key={r.id} className="hop-wrap">
          <span className="hop">
            <span className="rev">R{i}</span>
            {r.from_org_id ? orgShort(orgs.get(r.from_org_id)) : 'Note'}
            {r.is_interim ? ' · interim' : r.is_final ? ' · final' : ''} · {shortDate(r.received_on ?? r.created_at)}
          </span>
          <span className="arw" aria-hidden="true">
            →
          </span>
        </span>
      ))}
      {onus && !isClosed(item.status) ? (
        <span className="hop now">
          Onus now on {orgShort(onus)}
          {since !== null ? ` · ${since === 0 ? 'today' : plural(since, 'day')}` : ''}
        </span>
      ) : (
        <span className="hop">{isClosed(item.status) ? statusLabel(item.status, item.type) : 'No one has it'}</span>
      )}
    </div>
  );
}

export const shortDate = (v: string) => new Date(v.length <= 10 ? v + 'T00:00:00' : v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
